import { type ReactNode, type RefObject, useEffect, useRef } from 'react';
import { Button } from '../Button/Button';
import styles from './BulkActionBar.module.css';

export interface BulkActionBarProps {
    /** The region's name, sentence case: `Account actions`. */
    readonly label: string;
    /** The visible count text — `selection.summary`. */
    readonly summary: string;
    /** The `Select all <total> <plural noun>` follow-up, given only while `selection.canSelectAll`. */
    readonly selectAll?: { readonly label: string; readonly onSelect: () => void };
    /** `selection.clear`. */
    readonly onClear: () => void;
    /** Where focus goes after `Clear`, which unmounts the bar: the header checkbox. */
    readonly focusAfterClear: RefObject<HTMLElement | null>;
    /** The actions — `Button`s, e.g. **Change owner**. */
    readonly children: ReactNode;
    /** The bar's hook; its parts take `<id>-summary`, `<id>-select-all` and `<id>-clear`. */
    readonly 'data-testid'?: string;
}

/**
 * Design-system §6's bulk-action bar (*Data table (F32) → Bulk-action bar*): above the table while at
 * least one row is selected, on `surface/card-warm` with `radius/lg`, holding the count, the
 * consumer's actions and `Clear`. Built in `src/ui` by F5-h's offboarding lists (F5-h WI-1.3), for
 * F32-c's and F32-b's after it.
 *
 * It takes display props — the strings come from `useRowSelection` — so a consumer words its own
 * region name and actions (F5-h WI-1.3 plan Decision 6). A consumer renders it only while
 * `selection.count > 0`, directly above its table and outside the table's scroll region, so it stays put
 * while the table scrolls sideways; it sits in flow, not vertically sticky (F5-h WI-1.3 plan Decision 9).
 *
 * **Focus moves to the bar when it appears** (§6; F5-h NFR-4): the region itself takes focus on mount,
 * so a screen reader reads its name and count first, and keeps base.css's focus ring (F5-h WI-1.3 plan
 * Decisions 7 and 8). Two controls unmount the thing that was focused, so each hands focus on rather
 * than let it fall to `<body>` (WCAG 2.4.3): `Select all …` focuses the region again, and `Clear`
 * focuses `focusAfterClear`, the header checkbox, which stays mounted. Focus after a consumer's own
 * action is the consumer's to place.
 *
 * The count is not live: `SelectionAnnouncer` carries each change in one line.
 */
export function BulkActionBar({
    label,
    summary,
    selectAll,
    onClear,
    focusAfterClear,
    children,
    'data-testid': testId = 'bulk-action-bar',
}: BulkActionBarProps) {
    const regionRef = useRef<HTMLElement>(null);

    // On mount only: a later change of count must not pull focus from the row the user is on.
    useEffect(() => {
        regionRef.current?.focus();
    }, []);

    return (
        <section aria-label={label} className={styles.bar} data-testid={testId} ref={regionRef} tabIndex={-1}>
            <p className={styles.summary} data-testid={`${testId}-summary`}>
                {summary}
            </p>
            {selectAll === undefined ? null : (
                <Button
                    data-testid={`${testId}-select-all`}
                    onClick={() => {
                        selectAll.onSelect();
                        regionRef.current?.focus();
                    }}
                    size="sm"
                    variant="secondary"
                >
                    {selectAll.label}
                </Button>
            )}
            <div className={styles.actions}>{children}</div>
            <div className={styles.clear}>
                <Button
                    data-testid={`${testId}-clear`}
                    onClick={() => {
                        onClear();
                        focusAfterClear.current?.focus();
                    }}
                    size="sm"
                    variant="secondary"
                >
                    Clear
                </Button>
            </div>
        </section>
    );
}
